import {
  formattedDayOfTheWeekDayMonthShipmentDate,
  getNextSaturday,
  getNextSunday,
  getNextWorkingDay,
} from '../utils/dateAndTime';

import { NetworkHelpers } from '../helpers';
import * as POINT_TYPE from './pointTypes';

export const REQUIRED_TYPE = {
  MANDATORY: 'M',
  OPTIONAL: 'Y',
  NEEDLESS: 'N',
  DESCRIPTION: 'D',
};

export const NETWORK_KEYS = {
  NETWORK_KEY_10_30: '2^08', // 10:30
  NETWORK_KEY_12: '2^13', // 12:00
  NETWORK_KEY_NEXT_DAY: '2^12', // Next Day
  NETWORK_KEY_SHIP_TO_SHOP: '2^91', // Ship to Shop
  NETWORK_KEY_TWO_DAY: '2^11', // Two Day
  NETWORK_KEY_SAT: '2^71', // Saturday
  NETWORK_KEY_SAT_12: '2^17', // Sat by 12
  NETWORK_KEY_SUN: '2^75', // Sunday service
  NETWORK_KEY_SUN_12: '2^15', // Sun by 12
  NETWORK_KEY_EUROPE_ROAD: '2^19', // Parcel - DPD Europe By Road
  NETWORK_KEY_AIR_CLASSIC: '2^60', // Air Classic
  NETWORK_KEY_AIR_EXPRESS: '2^50', // Air Express
  NETWORK_KEY_DIRECT_LITE: '2^20', // DPD Direct Lite
};

export const WEEKEND_NETWORKS = [
  NETWORK_KEYS.NETWORK_KEY_SAT,
  NETWORK_KEYS.NETWORK_KEY_SAT_12,
  NETWORK_KEYS.NETWORK_KEY_SUN,
  NETWORK_KEYS.NETWORK_KEY_SUN_12,
];

export const PARCEL_ARRIVAL_DESCRIPTION = 'We’ll deliver ';

export const DELIVERY_SPEEDY_CONTENT_DOOR =
  'DPD Pickups usually deliver in 1 working day. Occasionally, due to collection schedules at your selected location, it might take 2-3 working days depending on the day and time after the collection.';
export const DELIVERY_SPEEDY_CONTENT =
  'DPD Pickups usually deliver in 1 working day. Occasionally, due to collection schedules at your selected location, it might take 2-3 working days depending on the day and time you drop-off your parcel.';
export const PARCEL_TWO_DAYS_DESCRIPTION_DROP_OFF = `It might take 2-3 working days depending on the day and time you drop-off your parcel.`;
export const PARCEL_TWO_DAYS_DESCRIPTION_COLLECT =
  'It might take 2-3 working days after the collection.';
const PARCEL_NEXT_DAY_DROP_OF =
  "We'll deliver your parcel the next day, depending on the day and time you drop off your parcel.";

export const TWO_DAY_DELIVERY_OPTIONS = {
  [POINT_TYPE.SHOP]: PARCEL_TWO_DAYS_DESCRIPTION_DROP_OFF,
  [POINT_TYPE.DOOR]: PARCEL_TWO_DAYS_DESCRIPTION_COLLECT,
};

export const NEXT_DAY_DELIVERY_OPTIONS = {
  [POINT_TYPE.SHOP]: () => PARCEL_NEXT_DAY_DROP_OF,
  [POINT_TYPE.DOOR]: shipmentDate =>
    `${PARCEL_ARRIVAL_DESCRIPTION} ${formattedDayOfTheWeekDayMonthShipmentDate(
      getNextWorkingDay(shipmentDate)
    )}`,
};

export const PARCEL_ARRIVAL_TIMES = {
  BEFORE_10_30: serviceDesc =>
    `Before ${NetworkHelpers.extractTimeFromServiceDesc(serviceDesc, '10:30')}`,
  BEFORE_12: serviceDesc =>
    `Before ${NetworkHelpers.extractTimeFromServiceDesc(serviceDesc, '12:00')}`,
  NEXT_DAY: () => 'Parcel Next day',
  SHIP_TO_SHOP: () => 'Speedy Delivery with a Heads Up',
  TWO_DAY: () => 'Two day Parcel',
  SAT: () => 'Parcel Saturday',
  SAT_12: serviceDesc =>
    `Saturday Before ${NetworkHelpers.extractTimeFromServiceDesc(
      serviceDesc,
      '12:00'
    )}`,
  SUN: () => 'Parcel Sunday',
  SUN_12: serviceDesc =>
    `Sunday Before ${NetworkHelpers.extractTimeFromServiceDesc(
      serviceDesc,
      '12:00'
    )}`,
};

export const NETWORK_DETAILS = {
  [NETWORK_KEYS.NETWORK_KEY_10_30]: {
    title: serviceDesc => PARCEL_ARRIVAL_TIMES.BEFORE_10_30(serviceDesc),
    desc: ({ shipmentDate, network }) =>
      `${PARCEL_ARRIVAL_DESCRIPTION} ${formattedDayOfTheWeekDayMonthShipmentDate(
        getNextWorkingDay(shipmentDate)
      )} before ${NetworkHelpers.extractTimeFromServiceDesc(
        network?.service?.serviceDesc,
        '10:30'
      )}`,
  },
  [NETWORK_KEYS.NETWORK_KEY_12]: {
    title: serviceDesc => PARCEL_ARRIVAL_TIMES.BEFORE_12(serviceDesc),
    desc: ({ shipmentDate, network }) =>
      `${PARCEL_ARRIVAL_DESCRIPTION} ${formattedDayOfTheWeekDayMonthShipmentDate(
        getNextWorkingDay(shipmentDate)
      )} before ${NetworkHelpers.extractTimeFromServiceDesc(
        network?.service?.serviceDesc,
        '12:00'
      )}`,
  },
  [NETWORK_KEYS.NETWORK_KEY_NEXT_DAY]: {
    title: () => PARCEL_ARRIVAL_TIMES.NEXT_DAY(),
    desc: ({ shipmentDate, collectFrom }) =>
      NEXT_DAY_DELIVERY_OPTIONS[collectFrom](shipmentDate),
  },
  [NETWORK_KEYS.NETWORK_KEY_SHIP_TO_SHOP]: {
    title: () => PARCEL_ARRIVAL_TIMES.SHIP_TO_SHOP(),
    desc: ({ collectFrom }) =>
      collectFrom === POINT_TYPE.DOOR
        ? DELIVERY_SPEEDY_CONTENT_DOOR
        : DELIVERY_SPEEDY_CONTENT,
  },
  [NETWORK_KEYS.NETWORK_KEY_TWO_DAY]: {
    title: () => PARCEL_ARRIVAL_TIMES.TWO_DAY(),
    desc: ({ collectFrom }) => TWO_DAY_DELIVERY_OPTIONS[collectFrom],
  },
  [NETWORK_KEYS.NETWORK_KEY_SAT]: {
    title: () => PARCEL_ARRIVAL_TIMES.SAT(),
    desc: ({ shipmentDate }) =>
      `${PARCEL_ARRIVAL_DESCRIPTION} ${formattedDayOfTheWeekDayMonthShipmentDate(
        getNextSaturday(shipmentDate)
      )}`,
  },
  [NETWORK_KEYS.NETWORK_KEY_SAT_12]: {
    title: serviceDesc => PARCEL_ARRIVAL_TIMES.SAT_12(serviceDesc),
    desc: ({ shipmentDate, network }) =>
      `${PARCEL_ARRIVAL_DESCRIPTION} ${formattedDayOfTheWeekDayMonthShipmentDate(
        getNextSaturday(shipmentDate)
      )} before ${NetworkHelpers.extractTimeFromServiceDesc(
        network?.service?.serviceDesc,
        '12:00'
      )}`,
  },
  [NETWORK_KEYS.NETWORK_KEY_SUN]: {
    title: () => PARCEL_ARRIVAL_TIMES.SUN(),
    desc: ({ shipmentDate }) =>
      `${PARCEL_ARRIVAL_DESCRIPTION} ${formattedDayOfTheWeekDayMonthShipmentDate(
        getNextSunday(shipmentDate)
      )}`,
  },
  [NETWORK_KEYS.NETWORK_KEY_SUN_12]: {
    title: serviceDesc => PARCEL_ARRIVAL_TIMES.SUN_12(serviceDesc),
    desc: ({ shipmentDate, network }) =>
      `${PARCEL_ARRIVAL_DESCRIPTION} ${formattedDayOfTheWeekDayMonthShipmentDate(
        getNextSunday(shipmentDate)
      )} before ${NetworkHelpers.extractTimeFromServiceDesc(
        network?.service?.serviceDesc,
        '12:00'
      )}`,
  },
};

export const DEFAULT_FIRST_IN_AREA = '9:00';
export const DEFAULT_LAST_IN_AREA = '18:00';

export const DEFAULT_ADDRESS_AFD_KEY = '50912063.0'; // DPD Oldbury with postcode B69 4DA
